import { useEffect, useState, useMemo, useCallback, useRef } from "react";
import { APIProvider } from "@vis.gl/react-google-maps";

import MapWithCentering from "./map/MapWithCentering";
import { MarkerEditorContainer, MarkerViewerContainer } from "./marker/MarkerContainers";
import LabelFilterMapControl from "../../labels/components/LabelFilterMapControl";
import LayerToggleControl from "./controls/LayerToggleControl";
import GeoJsonLayer from "./layers/GeoJsonLayer";
import MobileHamburgerMenu from "./controls/MobileHamburgerMenu";
import { ClusteredMarkers } from "./map/ClusteredMarkers";

import useMarkerLabel from "../hooks/marker/useMarkerLabel";
import { useMapEvents } from "../hooks/useMapEvents";
import { useProjectContext } from "../../../hooks/useProjectContext";
import { useProjectDetails } from "../hooks/useProjectDetails";
import { useMarkerStore } from '../hooks/marker/useMarkerStore';
import { usePinActions } from '../hooks/marker/usePinActions';
import { useEditMode } from "../hooks/useEditMode";
import { useMediaListStore } from "../../media/hooks/useMediaListStore";
import { useMarkerMediaActions } from "../../media/hooks/useMarkerMediaActions";
import { useTmpMediaList } from "../hooks/marker/useTmpMediaList";
import { useLabelFilter } from "../hooks/useLabelFilter";
import { usePinHandlers } from "../hooks/marker/usePinHandlers";
import useGoogleMapAPIKey from "../hooks/useGoogleMapAPIKey";

import { canEditMarker } from "../../../utils/permissions";
import { mapChromeShell } from "../../../styles/mapChromeShell";

const preloadThumbnails = (pins) => {
    pins
        .filter(pin => typeof pin.thumbnail_url === "string" && pin.thumbnail_url.trim() !== "")
        .map(pin => pin.thumbnail_url)
        .forEach(url => {
            const img = new Image();
            img.src = url;
        });
};

function MapContainer({
    defaultCenter,
    defaultZoom,
    loginUser,
    setIsUploadingMedia,
    setUploadProgress,
    isReadOnly = false,
    archiveLocations = null,
    labels = null,
    projectId: externalProjectId = null,
    publicUrl = null,
    // 上位コンポーネントが既に取得済みのプロジェクト情報。渡すとプロジェクトの再フェッチをスキップする
    project: preloadedProject = null,
    // 公開モード用: PublicMapPage から渡されるプロジェクト情報（usePublicProjectDetails の二重フェッチを防ぐ）
    kmlData: externalKmlData = null,
    useKml: externalUseKml = false,
    kmlTitle: externalKmlTitle = null,
}) {
    const contextProjectId = useProjectContext().projectId;
    const projectId = externalProjectId || contextProjectId;
    const googleMapAPIKey = useGoogleMapAPIKey();

    // 非公開モードのみプロジェクト詳細を取得（公開モードは PublicMapPage が担当）
    // preloadedProject が渡された場合はプロジェクトの再フェッチをスキップし、KMLデータのみ取得する
    const { project, kmlData: privateKmlData, useKml: privateUseKml } = useProjectDetails(publicUrl ? null : projectId, preloadedProject);
    const kmlData = publicUrl ? externalKmlData : privateKmlData;
    const useKml = publicUrl ? externalUseKml : privateUseKml;

    const { pins, setPins, currentPin, setCurrentPin, clearCurrentPin, tmpPin, setTmpPin, clearTmpPin } = useMarkerStore();
    const { refreshPins, savePin, deletePin } = usePinActions(projectId, { setPins, setCurrentPin });
    const refreshPinsRef = useRef(refreshPins);
    useEffect(() => { refreshPinsRef.current = refreshPins; }, [refreshPins]);

    const { isEditMode, enableEdit, disableEdit } = useEditMode();

    const { setMediaList, clearMediaList } = useMediaListStore();
    const { uploadAll } = useMarkerMediaActions(setMediaList);

    const { tmpMediaList, tmpAddFiles, tmpRemoveFileAt, tmpUpdateFileMeta, clearTmpMediaList } = useTmpMediaList();

    const [showGeoJson, setShowGeoJson] = useState(false);
    const toggleShowGeoJson = useCallback((value) => setShowGeoJson(value), []);

    const displayPins = useMemo(
        () => isReadOnly ? (archiveLocations || []) : pins,
        [isReadOnly, archiveLocations, pins]
    );

    useEffect(() => {
        if (!isReadOnly) refreshPinsRef.current();
    }, [projectId, isReadOnly]);

    useEffect(() => {
        if (displayPins.length > 0) preloadThumbnails(displayPins);
    }, [displayPins]);

    const { data: { labels: contextLabels } } = useMarkerLabel(publicUrl ? null : projectId);
    // contextLabels: null=未ロード / []=ロード済みで空 / 配列=ロード済み

    // ピンフィルターや色参照に使う配列（null のうちは空配列にフォールバック）
    const displayLabels = useMemo(() => {
        if (isReadOnly && labels) return labels;
        return contextLabels ?? [];
    }, [isReadOnly, labels, contextLabels]);

    // 子コンポーネントへは null をそのまま渡す（null = まだロード中 → 子が独自フェッチしない）
    const labelsForControls = useMemo(() => {
        if (isReadOnly && labels) return labels;
        return contextLabels; // null の可能性あり
    }, [isReadOnly, labels, contextLabels]);

    const { filteredLabelIds, handleFilterChange } = useLabelFilter(displayLabels, projectId, isReadOnly);

    const {
        handleNewPin,
        handleMarkerClick,
        handleCloseEditor,
        handleUpdateTmpPin,
        handleUpdateCurrentPin,
        handleSaveTmpPinWithMedia,
        handleSaveCurrentPinWithMedia,
        handleDeleteCurrentPin,
        handleDeleteTmpPinMedia,
    } = usePinHandlers({
        projectId,
        isEditMode, isReadOnly,
        tmpPin, setTmpPin, clearTmpPin,
        currentPin, setCurrentPin, clearCurrentPin,
        enableEdit, disableEdit,
        savePin, deletePin, uploadAll,
        tmpMediaList, tmpRemoveFileAt,
        clearMediaList, clearTmpMediaList,
        setIsUploadingMedia, setUploadProgress,
    });

    const { handlers: { handleMapClick } } = useMapEvents({ onMapClick: handleNewPin });

    const filteredPins = useMemo(() => {
        if (displayLabels.length === 0) return displayPins;
        if (filteredLabelIds.length === displayLabels.length) return displayPins;
        return displayPins.filter(pin => filteredLabelIds.includes(pin.labelId));
    }, [displayPins, filteredLabelIds, displayLabels.length]);

    if (!defaultCenter?.lat || !defaultCenter?.lng) {
        return <p>地図を初期化中...</p>;
    }

    const kmlTitle = externalKmlTitle || project?.kml_data?.title || "KMLファイル";

    return (
        <div className={mapChromeShell.wrapper}>
            <APIProvider apiKey={googleMapAPIKey}>
                {useKml && (
                    <LayerToggleControl
                        show={showGeoJson}
                        toggle={toggleShowGeoJson}
                        kmlTitle={kmlTitle}
                    />
                )}
                <LabelFilterMapControl
                    projectId={publicUrl ? null : Number(projectId)}
                    value={filteredLabelIds}
                    onChange={handleFilterChange}
                    isReadOnly={isReadOnly}
                    publicUrl={publicUrl}
                    labels={labelsForControls}
                />
                <MobileHamburgerMenu
                    projectId={publicUrl ? null : Number(projectId)}
                    filteredLabelIds={filteredLabelIds}
                    onFilterChange={handleFilterChange}
                    showGeoJson={showGeoJson}
                    onToggleGeoJson={toggleShowGeoJson}
                    isReadOnly={isReadOnly}
                    publicUrl={publicUrl}
                    useKml={useKml}
                    labels={labelsForControls}
                    kmlTitle={kmlTitle}
                />
                {showGeoJson && kmlData && <GeoJsonLayer data={kmlData} />}
                <MapWithCentering
                    defaultCenter={defaultCenter}
                    defaultZoom={defaultZoom}
                    selectedMarker={currentPin || tmpPin}
                    isEditable={isEditMode && !isReadOnly}
                    onClick={handleMapClick}
                >
                    <ClusteredMarkers
                        pins={filteredPins}
                        displayLabels={displayLabels}
                        onMarkerClick={handleMarkerClick}
                    />

                    {tmpPin && isEditMode && !isReadOnly && (
                        <MarkerEditorContainer
                            marker={tmpPin}
                            labelOptions={displayLabels}
                            onChange={handleUpdateTmpPin}
                            onSavePin={handleSaveTmpPinWithMedia}
                            onClose={handleCloseEditor}
                            onDelete={null}
                            tmpMediaList={tmpMediaList}
                            addMedia={tmpAddFiles}
                            uploadMedia={tmpUpdateFileMeta}
                            removeFileAt={tmpRemoveFileAt}
                            deleteMedia={handleDeleteTmpPinMedia}
                            setIsUploadingMedia={setIsUploadingMedia}
                            setUploadProgress={setUploadProgress}
                        />
                    )}

                    {currentPin && !tmpPin && (
                        isEditMode && !isReadOnly ? (
                            <MarkerEditorContainer
                                marker={currentPin}
                                labelOptions={displayLabels}
                                onChange={handleUpdateCurrentPin}
                                onSavePin={handleSaveCurrentPinWithMedia}
                                onClose={handleCloseEditor}
                                onDelete={handleDeleteCurrentPin}
                                setIsUploadingMedia={setIsUploadingMedia}
                                setUploadProgress={setUploadProgress}
                            />
                        ) : (
                            <MarkerViewerContainer
                                marker={currentPin}
                                labelOptions={displayLabels}
                                onClose={handleCloseEditor}
                                onEdit={isReadOnly ? null : () => enableEdit()}
                                canEdit={isReadOnly ? false : canEditMarker(loginUser, currentPin)}
                                publicUrl={publicUrl}
                            />
                        )
                    )}
                </MapWithCentering>
            </APIProvider>
        </div>
    );
}

export default MapContainer;
